import { ReactElement, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { Block, CropFree } from '@mui/icons-material'
import ContentCut from '@mui/icons-material/ContentCut'
import DeleteForever from '@mui/icons-material/DeleteForever'
import { Stack, Typography, Divider, Button } from '@mui/material'
import { useControlStore } from '@editor/stores'
import { ToggleButton, ToggleButtonDropDown, ToggleButtonDropDownHandle } from '@ui/actions'
import { publishClipPlaneCreateEvent } from '@ui/events'
import { useSelectionMode } from '@editorHooks'
import {
  CLIP_PLANE_POINT_SELECTION_KEY,
  CLIP_PLANE_POINT_SELECTION_MESSAGE,
} from 'src/components/scene/ClipController/constants'
import ClipPlaneImportHelperPreview from './ClipPlaneImportHelperPreview'
import ClipPlanePresetPreview from './ClipPlanePresetPreview'
import useCreateClipPlaneImportHelper from './useCreateClipPlaneImportHelper'
import { ClipPlaneHoverPreview, ClipPlanePreset, PRESET_CLIP_PLANES } from './utils'

const ClipPlaneControls = (): ReactElement => {
  const applyClipPlanes = useControlStore(state => state.applyClipPlanes)
  const clipPlanesInteractive = useControlStore(state => state.clipPlanesInteractive)
  const setApplyClipPlanes = useControlStore(state => state.setApplyClipPlanes)
  const setClipPlanesInteractive = useControlStore(state => state.setClipPlanesInteractive)
  const setPendingClipPlanePoints = useControlStore(state => state.setPendingClipPlanePoints)
  const resetClipPlanes = useControlStore(state => state.resetClipPlanes)
  const { selectionModeKey, setSelectionMode, unsetSelectionMode } = useSelectionMode()

  const isActive = selectionModeKey === CLIP_PLANE_POINT_SELECTION_KEY
  const dropdownRef = useRef<ToggleButtonDropDownHandle | null>(null)
  const [hoveredPreview, setHoveredPreview] = useState<ClipPlaneHoverPreview | null>(null)

  const createImportHelper = useCreateClipPlaneImportHelper()

  const handleCreateImportHelper = () => {
    createImportHelper()
    dropdownRef.current?.close()
  }

  const handleCreatePresetPlane = (preset: ClipPlanePreset) => {
    const { normal, point } = PRESET_CLIP_PLANES[preset]

    setPendingClipPlanePoints([])
    unsetSelectionMode()
    publishClipPlaneCreateEvent({ normal, point })
    dropdownRef.current?.close()
  }

  const handleStartAddPlane = () => {
    if (isActive) {
      unsetSelectionMode()
      return
    }

    setSelectionMode({
      key: CLIP_PLANE_POINT_SELECTION_KEY,
      message: CLIP_PLANE_POINT_SELECTION_MESSAGE,
      onAbort: () => {
        setPendingClipPlanePoints([])
      },
    })
  }

  useHotkeys('s', () => {
    setClipPlanesInteractive(!clipPlanesInteractive)
  })

  return (
    <>
      {hoveredPreview === 'import-helper' && <ClipPlaneImportHelperPreview />}
      {hoveredPreview && hoveredPreview !== 'import-helper' && (
        <ClipPlanePresetPreview preset={hoveredPreview} />
      )}
      <Stack direction="row" spacing={1}>
        <ToggleButtonDropDown
          ref={dropdownRef}
          hint="Neue Schnittfläche hinzufügen"
          Icon={ContentCut}
          data-cy="btn-add-clip-plane"
          extendTo="right"
        >
          <Stack spacing={1} minWidth={200}>
            <Typography variant="caption">
              Bei diesem Modus wird die Schnittfläche durch eine Auswahl von drei Punkten erstellt.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => {
                handleStartAddPlane()
                dropdownRef.current?.close()
              }}
              data-cy="btn-add-clip-plane-from-plane"
              size="small"
            >
              Aus Ebene
            </Button>
            <Divider />
            <Typography variant="caption">
              Bei diesem Modus wird eine Standard-Schnittfläche in der gewählten Ebene erstellt.
            </Typography>
            <Stack
              direction="row"
              spacing={0.5}
              onMouseLeave={() => {
                setHoveredPreview(null)
              }}
            >
              <Button
                variant="outlined"
                size="small"
                fullWidth
                data-cy="btn-add-clip-plane-on-z-plane"
                onMouseEnter={() => {
                  setHoveredPreview('xz')
                }}
                onClick={() => {
                  handleCreatePresetPlane('xz')
                }}
              >
                XZ
              </Button>
              <Button
                variant="outlined"
                size="small"
                fullWidth
                data-cy="btn-add-clip-plane-on-xy-plane"
                onMouseEnter={() => {
                  setHoveredPreview('xy')
                }}
                onClick={() => {
                  handleCreatePresetPlane('xy')
                }}
              >
                XY
              </Button>
              <Button
                variant="outlined"
                size="small"
                fullWidth
                data-cy="btn-add-clip-plane-on-yz-plane"
                onMouseEnter={() => {
                  setHoveredPreview('yz')
                }}
                onClick={() => {
                  handleCreatePresetPlane('yz')
                }}
              >
                YZ
              </Button>
            </Stack>
            <Divider />
            <Typography variant="caption">
              Bei diesem Modus werden zwei Schnittebenen in der z-Ebene erstellt, die jeweils das
              IFC-Modell und das importierte Modell schneiden, sodass einfach zu erkennen ist welche
              Elemente bereits importiert wurden.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onMouseEnter={() => {
                setHoveredPreview('import-helper')
              }}
              onMouseLeave={() => {
                setHoveredPreview(null)
              }}
              onClick={handleCreateImportHelper}
              data-cy="btn-add-clip-plane-import-helper"
            >
              Import helper
            </Button>
          </Stack>
        </ToggleButtonDropDown>
        <ToggleButton
          value="apply-clip-planes"
          hint="Schnittflächen anwenden"
          selected={applyClipPlanes}
          Icon={CropFree}
          onClick={() => {
            setApplyClipPlanes(!applyClipPlanes)
          }}
        />
        <ToggleButton
          value="toggle-clip-plane-interaction"
          hint="Schnittflächen nicht auswählbar"
          selected={!clipPlanesInteractive}
          Icon={Block}
          onClick={() => {
            setClipPlanesInteractive(!clipPlanesInteractive)
          }}
        />
        <ToggleButton
          value="reset-clip-planes"
          hint="Alle Schnittflächen entfernen"
          selected={false}
          Icon={DeleteForever}
          onClick={() => {
            setApplyClipPlanes(false)
            setPendingClipPlanePoints([])
            resetClipPlanes()
            if (isActive) {
              unsetSelectionMode()
            }
          }}
          data-cy="btn-clip-plane-reset"
        />
      </Stack>
    </>
  )
}

export default ClipPlaneControls
